import { useEffect, useState, useRef } from 'react'

/*
 * Garde-fou anti "ancien design qui réapparaît".
 *
 * Détecte qu'un nouveau déploiement est en ligne en comparant le hash du bundle
 * principal référencé par /index.html (servi avec must-revalidate) à celui chargé
 * au démarrage de la session. Si un nouveau build est détecté, affiche une bannière
 * non bloquante invitant à recharger — ce qui force la sortie d'un index.html /
 * bundle mis en cache côté client.
 *
 * No-op en dev (index.html pointe vers /src/main.jsx, pas vers /assets/index-*.js).
 */

async function fetchLatestBundleHash() {
  // cache:'no-store' → on récupère toujours l'index.html le plus frais de l'edge.
  const res = await fetch('/', { cache: 'no-store' })
  if (!res.ok) return null
  const html = await res.text()
  const m = html.match(/\/assets\/index-[\w-]+\.js/)
  return m ? m[0] : null
}

export default function VersionGuard() {
  const [stale, setStale] = useState(false)
  const initialHash = useRef(null)

  useEffect(() => {
    let cancelled = false

    const check = async () => {
      try {
        const hash = await fetchLatestBundleHash()
        if (!hash || cancelled) return
        if (initialHash.current === null) {
          // Première mesure : le bundle qui tourne actuellement.
          initialHash.current = hash
          return
        }
        if (hash !== initialHash.current) setStale(true)
      } catch {
        /* hors-ligne ou edge indisponible : on réessaiera plus tard */
      }
    }

    check()
    const id = setInterval(check, 5 * 60 * 1000) // toutes les 5 min
    const onVisible = () => { if (document.visibilityState === 'visible') check() }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  if (!stale) return null

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        padding: '8px 14px',
        background: 'var(--accent)', color: 'var(--accent-ink)',
        fontSize: '0.82rem', fontFamily: "'Inter', sans-serif",
        boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
      }}
    >
      <span>Une nouvelle version d’IAvocat.ai est disponible.</span>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: 'var(--accent-ink)', color: 'var(--accent)',
          border: 'none', borderRadius: 6, padding: '4px 12px',
          fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
        }}
      >
        Recharger
      </button>
    </div>
  )
}
